import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AnalyticsEvent,
  LinkActionResultTypenames,
  OpenPdfUrlAction,
  OpenWebUrlAction,
  PinwheelCardInput,
  PinwheelExperienceInput,
} from '@varo/graphql-schema-types';
import backgroundTextureSunset from 'assets/images/vxl/component/productIntroScreen/background-texture-sunset.png';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import TermsAndConditionsContent from 'features/billSwitch/components/TermsAndConditionsContent/TermsAndConditionsContent';
import UnfreezeCardsBottomSheet from 'features/billSwitch/components/UnfreezeCardsBottomSheet/UnfreezeCardsBottomSheet';
import { logErrorEvent } from 'features/billSwitch/helpers/logErrorEvent';
import useFetchBillSwitchProductIntroScreen from 'features/billSwitch/hooks/useFetchBillSwitchProductIntroScreen';
import { useOpenUrlDocumentScreen } from 'features/urlDocumentScreens/hooks/useOpenUrlDocumentScreen';
import React, { useContext, useEffect, useRef } from 'react';
import { ImageSourcePropType, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { computeBillsTotal } from 'shared/helpers/computeBillsTotal/computeBillsTotal';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useFetchCardsWithLockStatus } from 'shared/hooks/useFetchCardsWithLockStatus/useFetchCardsWithLockStatus';
import { UnknownGQLType } from 'shared/legacySDUIAdapters/genericRouteAction';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

const BillSwitchSweepstakesScreen = () => {
  const { defaultTitle, defaultMessage, retryButtonTitle, doneButtonTitle } =
    useDefaultErrorStrings();

  const { result, error, fetching, refetch } =
    useFetchBillSwitchProductIntroScreen();

  const { initiatePinwheel, isPinwheelLoading } = usePinwheel();

  const { segmentClient } = useAnalyticsConfiguration();

  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const { top } = useSafeAreaInsets();
  const router = useRouter();

  const { openWebUrlScreen, openPdfDocumentScreen } =
    useOpenUrlDocumentScreen();

  const {
    cards,
    hasCards,
    hasLockedCards,
    analyticsMetadata: { isDebitLocked, isBelieveLocked },
    cardsError,
    cardsFetching,
    refetchCards,
  } = useFetchCardsWithLockStatus();
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  useViewDidFocusLogScreenAnalytics({
    name: 'Bill Switch Product Intro',
    properties: {
      category: 'bill_switch',
      source_name: 'home_sweepstakes',
    },
  });
  const canLaunchPinwheel = hasCards && !hasLockedCards;

  const launchPinwheel = () => {
    if (!canLaunchPinwheel) {
      return;
    }
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.BillSwitch,
        cardOptions: [PinwheelCardInput.Debit, PinwheelCardInput.Believe],
      },
      sourceName: 'home_sweepstakes',
      onLinkTokenError: () => {
        logErrorEvent({
          segmentClient,
          source_name: 'home_sweepstakes',
          error_message: '[Link Token Error]: Link token generation failed',
        });

        setSnackBarMessage?.(defaultMessage);
      },
      onOutcome: ({ sessionState }) => {
        const { billSwitches } = sessionState ?? {};
        if (billSwitches?.length) {
          const amount = computeBillsTotal(billSwitches);
          router.replace({
            pathname: '/bill-switch/funding',
            params: { amt: amount },
          });
        }
      },
    });
  };

  useEffect(() => {
    if (error) {
      logErrorEvent({
        segmentClient,
        source_name: 'home_sweepstakes',
        error_message:
          '[Fetch Sweepstakes Error]: ' +
          (error.message ?? error.title ?? 'Unknown error'),
      });
    }
  }, [error, segmentClient]);

  useEffect(() => {
    if (cardsError) {
      logErrorEvent({
        segmentClient,
        source_name: 'home_sweepstakes',
        error_message:
          '[Fetch Cards Error]: ' +
          (cardsError.message ?? cardsError.title ?? 'Unknown error'),
      });
    }
  }, [cardsError, segmentClient]);

  const handleTermsAndConditionsLink = (action: Maybe<UnknownGQLType>) => {
    const sendEvent = (analyticsEvent: Maybe<AnalyticsEvent>) => {
      if (analyticsEvent) {
        const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
        void segmentClient.logEvent(name, properties);
      }
    };

    if (action?.__typename === LinkActionResultTypenames.OpenPdfUrlAction) {
      const { url, analyticsEvent } = action as OpenPdfUrlAction;
      sendEvent(analyticsEvent);
      openPdfDocumentScreen(url);
    } else if (
      action?.__typename === LinkActionResultTypenames.OpenWebUrlAction
    ) {
      const { url, analyticsEvent } = action as OpenWebUrlAction;
      sendEvent(analyticsEvent);
      openWebUrlScreen(url);
    }
  };

  const { headlineText, primaryButtonText, body } = result ?? {};

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
              transparent
            />
          ),
        }}
      />
      {result ? (
        <ProductIntroScreen
          visual="BILLS_A"
          headlineTextStyle="expressive"
          backgroundTexture={backgroundTextureSunset as ImageSourcePropType}
          headlineText={headlineText}
          topPadding={70}
          primaryButton={
            primaryButtonText
              ? {
                  label: primaryButtonText,
                  onPress: () => {
                    if (!canLaunchPinwheel) {
                      bottomSheetRef?.current?.present();
                    } else {
                      launchPinwheel();
                    }
                  },
                  isLoading: isPinwheelLoading,
                  analyticsId: 'launchPinwheel',
                  customAttributes: {
                    source_name: 'home_sweepstakes',
                    debit_locked: isDebitLocked,
                    believe_locked: isBelieveLocked,
                  },
                }
              : undefined
          }
          bodyContent={
            body ? (
              <TermsAndConditionsContent
                upperText={body.upperText}
                bulletsTitle={body.bulletsTitle}
                bulletPoints={body.bulletPoints}
                lowerText={body.lowerText}
                disclosureText={body.disclosureText}
                navigateForAction={handleTermsAndConditionsLink}
              />
            ) : null
          }
        />
      ) : null}
      <UnfreezeCardsBottomSheet
        ref={bottomSheetRef}
        hasLockedCards={hasLockedCards}
        hasCards={hasCards}
        cards={cards}
        analyticsMetadata={{ source_name: 'home_sweepstakes' }}
        onSuccess={() => {
          bottomSheetRef?.current?.dismiss();
          launchPinwheel();
        }}
        onDismiss={() => bottomSheetRef?.current?.dismiss()}
      />
      <ScreenTransitionOverlay
        isLoading={fetching || cardsFetching}
        LoadingComponent={
          <View style={{ paddingTop: top + theme.spacing.m }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={
          error || cardsError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={
                error?.title ?? cardsError?.title ?? defaultTitle
              }
              messageBody={
                error?.message ?? cardsError?.message ?? defaultMessage
              }
              primaryButton={{
                title: doneButtonTitle,
                onPress: navigateBack,
              }}
              secondaryButton={{
                title: retryButtonTitle,
                onPress: () => {
                  if (error) {
                    refetch();
                  }
                  if (cardsError) {
                    refetchCards();
                  }
                },
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default BillSwitchSweepstakesScreen;
